<script lang="ts">
	import Button from "../Button.svelte";
	import { DateTime } from "../../../../core/utils/luxon.js";
	import LabelledDate from "../Form/LabelledDate.svelte";
	import cake from "../../../../core/assets/icons/cake.svg?raw";
	import LabelledInput from "../Form/LabelledInput.svelte";
	import Checkbox from "../Form/Checkbox.svelte";
	import Form from "../Form/Form.svelte";
	import { getGlobalSettings } from "../../getGlobalSettings.js";
	import { createPromiseStore } from "../../../../core/utils/createPromiseStore.js";
	import { getClients } from "../../appsync/getClients.js";
	import sendInquiry from "../../appsync/mutations/sendInquiry.graphql";
	import Modal from "../Modal.svelte";
	import Smartform from "../Smartform.svelte";
	import type { InquiryCustomCake } from "../../../../core/schema/InquiryCustomCake.js";
	import getCustomCakeInquiryDatesQuery from "../../appsync/queries/getCustomCakeSetting.graphql";
	import { onMount } from "svelte";
	import Preloader from "../Preloader.svelte";
	import LabelledRadioInput from "../Form/LabelledRadioInput.svelte";
	import { InquiryCustomCakeSize } from "../../../../core/schema/InquiryCustomCakeSize.js";
	import { inquiryCustomCakeSizeNameMap } from "../../../../core/schema/names/inquiryCustomCakeSizeNameMap.js";
	import { InquiryCustomCakeFilling } from "../../../../core/schema/InquiryCustomCakeFilling.js";
	import { inquiryCustomCakeFillingNameMap } from "../../../../core/schema/names/inquiryCustomCakeFillingNameMap.js";
	import FileUploader from "../Form/FileUploader.svelte";
	import { acceptedStaticImages } from "../../../../core/schema/acceptedStaticImages";
	import type { UploadedFile } from "../../../../core/schema/UploadedFile";
	import GdprCheckbox from "../GdprCheckbox.svelte";
	import { InquiryType } from "../../../../core/schema/InquiryType.js";
	import { initSentry } from "../../utils/initSentry.js";
	import * as Sentry from "@sentry/browser";
	import { expectErrorMessage } from "../Form/expectErrorMessage.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import Message from "../Message.svelte";
	import Link from "../Link.svelte";
	import { categoryPathMap } from "../../../../core/schema/paths/categoryPathMap";
	import { inquiryCustomCakeDeliveryTypeNameMap } from "../../../../core/schema/names/inquiryCustomCakeDeliveryTypeNameMap.js";
	import { InquiryCustomCakeDeliveryType } from "../../../../core/schema/InquiryCustomCakeDeliveryType.js";
	import {
		type SmartFormAddressResult,
		SmartFormAddressValidationResultType,
	} from "../../../../core/schema/Smartform.js";
	import type { SmartFormAddress } from "../../../../core/schema/SmartFormAddress.js";
	import Select from "../Form/Select.svelte";
	import { getAvailableDeliveryTimes } from "../../../../core/schema/utils/getAvailableDeliveryTimes";
	import type { InquiryCreateInputOfType } from "../../../../core/schema/public/InquiryCreateInput.js";

	export let enableDeliveryToAddress: boolean;

	let asCompany = false;
	let selectedDate: Date | undefined;
	let customCakeDates: string[] = [];

	let selectedDeliveryType: InquiryCustomCake["deliveryType"] | undefined;
	let selectedSize: InquiryCustomCake["size"] | undefined;
	let selectedFilling: InquiryCustomCake["filling"] | undefined;
	let selectedFiles: UploadedFile[] | undefined;

	const globalSettings = getGlobalSettings();

	const { TENANT_ID, TENANT_LOCALE } = globalSettings;

	const send = createPromiseStore(async (input: InquiryCreateInputOfType<InquiryType.CustomCake>) => {
		const { graphQL } = await getClients(globalSettings);
		await graphQL.mutate<{ input: InquiryCreateInputOfType<InquiryType.CustomCake> }>({
			mutation: sendInquiry,
			variables: { input },
		});
	});

	const getCustomCakeDates = createPromiseStore(async () => {
		const { graphQL } = await getClients(globalSettings);
		return await graphQL.query<string[]>({
			query: getCustomCakeInquiryDatesQuery,
			variables: {},
		});
	});

	function setPhotoFile({ detail: { files } }: CustomEvent<{ files: UploadedFile[] | undefined }>): void {
		selectedFiles = files;
	}

	onMount(async () => {
		customCakeDates = (await getCustomCakeDates.call()) ?? [];
	});

	const min = DateTime.now();
	const max = DateTime.now().plus({ years: 2 });

	let smartFormDeliveryAddress: SmartFormAddress | undefined = undefined;
	function onAddressValidation({ detail }: CustomEvent<SmartFormAddressResult>): void {
		if (detail.instanceId === "smartform-instance-delivery") {
			const [address] = detail.result.addressArray;
			if (detail.result.type !== SmartFormAddressValidationResultType.HIT || !address) {
				smartFormDeliveryAddress = undefined;
				return;
			}

			smartFormDeliveryAddress = address;
		}
	}

	$: sendErrorMessage =
		$send.status === "error" &&
		expectErrorMessage(
			$send.error,
			TENANT_LOCALE,
			{},
			"Chyba při odesílání poptávky. Zkuste to prosím znovu nebo později.",
		);

	let showSuccessModal = false;
	async function submit(event: SubmitEvent): Promise<void> {
		sendErrorMessage = undefined;
		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const deliveryType = formData.get("deliveryType") as InquiryCustomCake["deliveryType"];
		if (deliveryType === InquiryCustomCakeDeliveryType.DeliveryToAddress) {
			if (!smartFormDeliveryAddress) {
				sendErrorMessage = "Vyplňte validní adresu.";
				return;
			}
		}

		const input: InquiryCreateInputOfType<InquiryType.CustomCake> = {
			fullName: String(formData.get("fullName")),
			email: String(formData.get("email")),
			phoneNumber: String(formData.get("phoneNumber")),
			type: InquiryType.CustomCake,
			date: String(formData.get("date")),
			deliveryTime: String(formData.get("deliveryTime")),
			customCakeDetail: {
				celebrantAge: String(formData.get("celebrantAge")),
				celebrantName: String(formData.get("celebrantName")),
				figurine: String(formData.get("figurine")),
				filling: formData.get("filling") as InquiryCustomCake["filling"],
				images: selectedFiles ?? [],
				deliveryType,
				shippingAddress:
					deliveryType === InquiryCustomCakeDeliveryType.DeliveryToAddress
						? {
								city: String(formData.get("shippingAddressCity")),
								street: String(formData.get("shippingAddressStreet")),
								streetNumber: String(formData.get("shippingAddressStreetNumber")),
								zip: String(formData.get("shippingAddressZip")),
								smartFormAddress: smartFormDeliveryAddress,
							}
						: undefined,
				size: formData.get("size") as InquiryCustomCake["size"],
				theme: String(formData.get("theme")),
			},
			billingAddress: {
				street: String(formData.get("street")),
				streetNumber: String(formData.get("streetNumber")),
				city: String(formData.get("city")),
				zip: String(formData.get("zip")),
			},
			...(asCompany && {
				company: {
					name: String(formData.get("cmpName")),
					cin: String(formData.get("cmpCin")),
					vat: String(formData.get("cmpVat")),
				},
			}),
			note: String(formData.get("note")),
			tenantId: TENANT_ID,
			gdprApprovedAt: String(formData.get("gdprApprovedAt")),
		};

		try {
			await send.call(input);
			form.reset();
			showSuccessModal = true;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			await initSentry(globalSettings);
			Sentry.captureException(error);
		}
	}

	function createExcludedDays(customCakeDates: string[]): string[] {
		const excludedDays: string[] = [];
		let dateTime = DateTime.now();
		if (dateTime.hour >= 14) {
			excludedDays.push(dateTime.toISODate());
			dateTime = dateTime.plus({ days: 1 });
		}
		while (dateTime.startOf("day").toMillis() <= max.startOf("day").toMillis()) {
			if (!customCakeDates.includes(dateTime.toISODate())) {
				excludedDays.push(dateTime.toISODate());
			}
			dateTime = dateTime.plus({ days: 1 });
		}
		return excludedDays;
	}

	$: deliveryTimes = selectedDate
		? getAvailableDeliveryTimes(DateTime.now().plus({ hours: 1 }).startOf("hour").toISO(), selectedDate)
		: [];

	$: excludedDays = createExcludedDays(customCakeDates);
</script>

<Modal class="!max-w-[36rem]" show={showSuccessModal}>
	<p class="h2 sm:ml-12" slot="title">Děkujeme za poptávku</p>
	<div class="flex flex-col items-center gap-2">
		<p>Vaše poptávka byla úspěšně odeslána. Brzy se vám ozveme.</p>

		<Button
			on:click={async () => {
				showSuccessModal = false;
				customCakeDates = (await getCustomCakeDates.call()) ?? [];
			}}
		>
			Rozumím
		</Button>
	</div>
</Modal>

{#if $getCustomCakeDates.status === "loading"}
	<Preloader />
{:else if $getCustomCakeDates.status === "success"}
	{#if $getCustomCakeDates.result && $getCustomCakeDates.result.length > 0}
		{#if selectedDate}
			<p class="mb-10 text-center font-semibold">
				{nbspify("Vyplňte, prosím, tento jednoduchý formulář. Brzy se vám ozveme zpět!")}
			</p>
		{:else}
			<div class="mb-10">
				<p class="mx-auto max-w-xl text-center">Ověřte prosím, zda máme pro výrobu dortu volnou kapacitu.</p>
				<p class="mx-auto mt-2 max-w-xl text-center">
					Pokud je váš termín obsazený, nezoufejte! Určitě vás nenecháme ve štychu. Mrkněte na naši stálou
					<Link
						class="text-secondary hover:text-primary hover:decoration-primary  font-semibold transition-colors duration-300"
						href="/{categoryPathMap.cakes[TENANT_LOCALE]}"
					>
						nabídku dortů
					</Link>, které lze narozeninově přizdobit, možnosti najdete v sekci úpravy.
				</p>
			</div>
		{/if}
		<Smartform on:addressValidation={onAddressValidation}>
			<Form onsubmit={submit} class="flex flex-col items-center gap-8" enableRecaptcha>
				<div class="flex items-end gap-5">
					<LabelledDate
						class="[&>input]:font-semibold"
						id="date"
						{excludedDays}
						required
						min={min.toJSDate()}
						max={max.toJSDate()}
						withoutDayOfTheWeek
						bind:value={selectedDate}
					>
						Datum vyzvednutí
					</LabelledDate>
					<div class="min-w-20">
						Čas vyzvednutí
						<Select
							id="deliveryTime"
							options={deliveryTimes.map((time) => ({
								value: time,
								label: time,
							}))}
							required
							disabled={!selectedDate}
						/>
					</div>
				</div>
				<div class="contents" class:hidden={!selectedDate}>
					<div class="w-full">
						<p>Velikost</p>
						<div class="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
							{#each Object.values(InquiryCustomCakeSize) as size}
								<LabelledRadioInput id="size-{size}" name="size" value={size} bind:group={selectedSize} required>
									{inquiryCustomCakeSizeNameMap[size][TENANT_LOCALE]}
								</LabelledRadioInput>
							{/each}
						</div>
					</div>

					<div class="w-full">
						<p>Náplň</p>
						<div class="mt-4 grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 md:grid-cols-3">
							{#each Object.values(InquiryCustomCakeFilling) as filling}
								<LabelledRadioInput
									id="size-{filling}"
									name="filling"
									value={filling}
									bind:group={selectedFilling}
									required
								>
									{inquiryCustomCakeFillingNameMap[filling][TENANT_LOCALE]}
								</LabelledRadioInput>
							{/each}
						</div>
					</div>

					<div class="w-full">
						<p>Způsob doručení</p>
						<div class="mt-4 flex flex-wrap gap-x-6 gap-y-3 sm:flex-nowrap">
							{#each Object.values(InquiryCustomCakeDeliveryType).filter((type) => type !== InquiryCustomCakeDeliveryType.DeliveryToAddress || enableDeliveryToAddress) as value}
								<LabelledRadioInput
									id="deliveryType-{value}"
									name="deliveryType"
									{value}
									bind:group={selectedDeliveryType}
									required
								>
									<div class="flex w-full flex-row justify-between">
										{inquiryCustomCakeDeliveryTypeNameMap[value][TENANT_LOCALE]}
									</div>
								</LabelledRadioInput>
							{/each}
						</div>

						{#if selectedDeliveryType === InquiryCustomCakeDeliveryType.DeliveryToAddress}
							<p class="mt-4">Místo doručení</p>
							<div class="mt-2 grid grid-cols-2 gap-x-6 gap-y-3">
								<LabelledInput
									id="shippingAddressStreet"
									name="shippingAddressStreet"
									inputProps={{ class: "smartform-instance-delivery smartform-address-street" }}
									required
								>
									Ulice
								</LabelledInput>
								<LabelledInput
									id="shippingAddressStreetNumber"
									name="shippingAddressStreetNumber"
									inputProps={{ class: "smartform-instance-delivery smartform-address-number" }}
									required
								>
									Číslo popisné
								</LabelledInput>
								<LabelledInput
									id="shippingAddressCity"
									name="shippingAddressCity"
									inputProps={{ class: "smartform-instance-delivery smartform-address-city" }}
									required
								>
									Město
								</LabelledInput>
								<LabelledInput
									id="shippingAddressZip"
									name="shippingAddressZip"
									inputProps={{ class: "smartform-instance-delivery smartform-address-zip" }}
									required
								>
									PSČ
								</LabelledInput>
							</div>
						{/if}
					</div>

					<div class="w-full">
						<p>Informace k objednávce</p>

						<div class="mt-4 grid w-full items-end gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-rows-2">
							<LabelledInput id="theme">Téma dortu</LabelledInput>
							<LabelledInput id="figurine">Figurka</LabelledInput>
							<LabelledInput id="celebrantName">Jméno oslavence</LabelledInput>
							<LabelledInput id="celebrantAge">Věk oslavence</LabelledInput>
						</div>
					</div>

					<div class="w-full">
						<p>Osobní údaje</p>
						<div class="mt-4 grid w-full items-end gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-rows-2">
							<LabelledInput id="fullName" required autocomplete="name">Jméno a příjmení</LabelledInput>
							<LabelledInput
								id="phoneNumber"
								required
								type="tel"
								autocomplete="tel"
								inputProps={{ pattern: "[+]?[\\d ]+", title: "Telefonní číslo" }}
							>
								Telefon
							</LabelledInput>
							<LabelledInput id="email" required type="email" autocomplete="email">E-mail</LabelledInput>
						</div>
					</div>

					<div class="mt-2 w-full">
						<div class="grid w-full items-end gap-x-6 gap-y-3 lg:grid-flow-col lg:grid-rows-3">
							<p class="mb-2">Fakturační údaje</p>

							<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-[1fr,7.5rem]">
								<LabelledInput
									id="street"
									required
									autocomplete="billing address-line1"
									inputProps={{ class: "smartform-company-address-street" }}
								>
									Ulice
								</LabelledInput>
								<LabelledInput
									id="streetNumber"
									required
									autocomplete="billing address-line2"
									inputProps={{ class: "smartform-company-address-number" }}
								>
									čp.
								</LabelledInput>
							</div>
							<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-[1fr,7.5rem]">
								<LabelledInput
									id="city"
									required
									autocomplete="billing address-level2"
									inputProps={{ class: "smartform-company-address-city" }}
								>
									Město
								</LabelledInput>
								<LabelledInput
									id="zip"
									required
									minlength={3}
									autocomplete="billing postal-code"
									inputProps={{ class: "smartform-company-address-zip" }}
								>
									PSČ
								</LabelledInput>
							</div>

							<Checkbox class="mb-2" id="asCompany" bind:checked={asCompany}>Fakturuji na firmu</Checkbox>
							<div
								class="contents {asCompany
									? ''
									: '[&>*]:pointer-events-none [&>*]:absolute [&>*]:opacity-0 [&>*]:lg:relative'}"
							>
								<LabelledInput
									id="cmpName"
									required
									disabled={!asCompany}
									inputProps={{ class: "smartform-company-name" }}
								>
									Název firmy
								</LabelledInput>
								<div class="row-span-2 grid gap-3 sm:row-span-1 sm:grid-cols-2">
									<LabelledInput
										id="cmpCin"
										required
										disabled={!asCompany}
										inputProps={{
											pattern: "\\d+",
											class: "smartform-company-registration-number",
										}}
									>
										IČO
									</LabelledInput>
									<LabelledInput
										id="cmpVat"
										disabled={!asCompany}
										inputProps={{ class: "smartform-company-vat-number" }}
									>
										DIČ
									</LabelledInput>
								</div>
							</div>
						</div>
					</div>

					<div class="mt-2 w-full">
						<p class="mb-2">Ukázkové fotky (max 3)</p>
						<FileUploader
							imagesInRow
							accept={acceptedStaticImages}
							destinationFolder="order-custom-cake-photos"
							bucket="upload"
							multiple
							maximum={3}
							on:change={setPhotoFile}
						/>
					</div>

					<div class="relative w-full">
						<textarea
							id="note"
							name="note"
							class="input-with-label peer h-[15.5625rem] w-full rounded-[.25rem] border border-gray-200 px-3 !pb-4 !pt-[2.25rem] text-base focus:font-semibold focus:outline-none lg:px-6"
							placeholder=" "
						></textarea>
						<label class="label textarea-label" for="note">Poznámka k objednávce</label>
						<p class="text-2xs mt-3">*Povinné údaje</p>
					</div>

					<GdprCheckbox />

					<Button
						class="sm:!text-md mx-auto !rounded-full !px-8 !py-2 sm:!px-12 sm:!py-4 lg:mx-0 xl:!px-28 xl:!py-[1.125rem] xl:!text-[1.375rem]"
						type="submit"
						icon={cake}
						iconClass="h-6"
						disabled={$send.status === "loading"}
					>
						{#if $send.status === "loading"}
							Odesílám…
						{:else}
							Odeslat formulář
						{/if}
					</Button>

					{#if sendErrorMessage}
						<Message variant="error">
							{nbspify(sendErrorMessage)}
						</Message>
					{/if}
				</div>
			</Form>
		</Smartform>
	{:else}
		<div
			class="mx-auto grid w-fit grid-cols-[auto_1fr] items-center gap-4 rounded-[22px] bg-gray-100 px-12 py-4 text-center"
		>
			<span class="text-md bg-primary flex h-8 w-8 items-center justify-center rounded-full font-semibold text-white"
				>i</span
			>
			<p class="text-xs">Omlouváme se, aktuálně není dostupný žádný termín.</p>
		</div>
	{/if}
{:else}
	<div
		class="mx-auto grid w-fit grid-cols-[auto_1fr] items-center gap-4 rounded-[22px] bg-gray-100 px-12 py-4 text-center"
	>
		<span class="text-md bg-primary flex h-8 w-8 items-center justify-center rounded-full font-semibold text-white"
			>i</span
		>
		<p class="text-xs">
			Něco se nepovedlo. Prosím, kontaktujte nás na naší e-mailové adrese <a href="mailto:objednavky@sladkadilna.cz">
				objednavky@sladkadilna.cz
			</a>
		</p>
	</div>
{/if}
